// Animated presentation → DOM styles (framework-free; shared by the Studio and
// the served runtime since 7.6H, next to style.ts). The VALUES come from
// the Creative Engine's pure sampler (`sampleAnimations`, `inTimingWindow`);
// this module only maps them to a CSS transform/opacity and writes them
// imperatively, so playback never re-renders React per frame.

import {
  inTimingWindow,
  sampleAnimations,
  type AnimatedValues,
  type AnimationTrack,
  type CreativeDocument,
} from "@interactads/creative-model";
import { resolveVariant } from "@interactads/creative-model";

export interface AnimatedTarget {
  readonly id: string;
  readonly tracks: readonly AnimationTrack[];
  readonly timing: { readonly start?: number; readonly end?: number } | undefined;
  readonly opacity: number;
  readonly rotation: number;
  readonly origin: { readonly x: number; readonly y: number };
}

/** Per-element animation inputs of one variant (resolved by the engine: override tracks replace base tracks). */
export function animatedTargets(doc: CreativeDocument, layoutId: string): Map<string, AnimatedTarget> {
  const out = new Map<string, AnimatedTarget>();
  const resolved = resolveVariant(doc, layoutId);
  if (!resolved) return out;
  for (const el of doc.elements) {
    const p = resolved.elements[el.id] as
      { frame?: { rotation?: number; origin?: { x: number; y: number } }; animations?: AnimationTrack[] } | undefined;
    if (!p) continue;
    out.set(el.id, {
      id: el.id,
      tracks: p.animations ?? [],
      timing: el.timing,
      opacity: el.opacity ?? 1,
      rotation: p.frame?.rotation ?? 0,
      origin: p.frame?.origin ?? { x: 0.5, y: 0.5 },
    });
  }
  return out;
}

export interface SampledStyle {
  readonly transform: string;
  readonly transformOrigin: string;
  /** Effective opacity (element opacity × animated opacity). */
  readonly opacity: number;
  /** Inside the element's visibility window. */
  readonly inWindow: boolean;
  readonly values: AnimatedValues;
}

export function sampleTarget(
  target: AnimatedTarget,
  sceneTime: number,
  options: { readonly sceneEnd?: number | null; readonly manualStarts?: Readonly<Record<string, number>> } = {}
): SampledStyle {
  const values = sampleAnimations(target.tracks, sceneTime, options);
  const rotation = target.rotation + values.rotation;
  return {
    transform: `translate(${String(values.offsetX)}px, ${String(values.offsetY)}px) rotate(${String(rotation)}deg) scale(${String(values.scaleX)}, ${String(values.scaleY)})`,
    transformOrigin: `${String(target.origin.x * 100)}% ${String(target.origin.y * 100)}%`,
    opacity: Math.round(target.opacity * values.opacity * 10_000) / 10_000,
    inWindow: inTimingWindow(target.timing, sceneTime),
    values,
  };
}

/** Opacity used in the EDITOR for a layer that is not visible at the current instant (still selectable). */
export const GHOST_OPACITY = 0.25;

/**
 * Writes the sampled state of every animated layer under `root` (nodes marked
 * data-element-id). Editor mode never hides a layer: invisible-at-this-instant
 * layers are drawn as ghosts so they can still be selected and edited.
 */
export function applySampledStyles(
  root: HTMLElement,
  targets: ReadonlyMap<string, AnimatedTarget>,
  sceneTime: number,
  mode: "editor" | "preview",
  options: {
    readonly sceneEnd?: number | null;
    readonly manualStarts?: ReadonlyMap<string, Readonly<Record<string, number>>>;
  } = {}
): void {
  for (const node of root.querySelectorAll<HTMLElement>("[data-element-id]")) {
    const target = targets.get(node.dataset.elementId ?? "");
    if (!target) continue;
    // Every layer is written (also static ones), so nothing stale survives an edit that removed a track.
    const manual = options.manualStarts?.get(target.id);
    const s = sampleTarget(target, sceneTime, {
      sceneEnd: options.sceneEnd ?? null,
      ...(manual ? { manualStarts: manual } : {}),
    });
    node.style.transform = s.transform;
    node.style.transformOrigin = s.transformOrigin;
    if (mode === "editor") {
      const hiddenInAd = node.dataset.hiddenInAd === "true";
      const ghost = hiddenInAd || !s.inWindow || s.opacity < 0.12;
      node.style.opacity = String(ghost ? GHOST_OPACITY : s.opacity);
      node.dataset.ghost = ghost ? "true" : "";
    } else {
      node.style.opacity = String(s.opacity);
      node.style.visibility = s.inWindow ? "" : "hidden";
    }
  }
}
